import {FormControl, FormGroup} from '@angular/forms';

import {BaseControl, CheckboxControl, SelectControl} from 'appShared/classes/controls';
import {
    ClientFeedSubscriptionModel,
    FeedPriceTypesEnum,
    HttpStatusCodesEnum,
    MarketDataFeedBundleInterface,
    ServerResponse,
    ServerResponseData,
    SesHelper,
    SesService,
    UserAccountSubscriptionInputModel
} from '@spa/api';
import {Helper} from 'appShared/classes';
import {map} from 'rxjs/operators';
import {CreateBundleSubscriptionActivationDateSelector, CreateBundleSubscriptionControls, CreateBundleSubscriptionCurrencySelector, CreateBundleSubscriptionPriceSelector, } from 'appAssets/descriptors';
import {
    MarketDataFeedBundleFormErrorStatesEnum,
    MarketDataFeedSubscriptionFormErrorStatesEnum
} from 'appEntries/panel/enums';
import {ColorCodesEnum, ComponentTypesEnum} from 'appShared/enums';
import {BaseMarketDataForm} from 'appShared/forms';
import { CurrencyService } from 'appShared/services';
import { CurrencyTrait } from 'appEntries/panel/traits';
import { InArrayValidator } from '@spa/validators';

export class CreateSubscriptionForm extends BaseMarketDataForm {
    private readonly offsetAccountId: string;
    private readonly clientAccountAndBundleData: ClientFeedSubscriptionModel;
    private readonly bundles: MarketDataFeedBundleInterface[];
    private readonly MIN_SELECTED_BUNDLE_CCOUNT = 1;
    public readonly FEEDID_CHECKBOX_PREFIX = 'bundle_';
    public readonly FORM_ELEMENT_CCOUNT = 2;
    private readonly currencyService: CurrencyService;

    constructor(
        sesService: SesService,
        currencyService: CurrencyService,
        bundles: MarketDataFeedBundleInterface[],
        clientAccountAndBundleData: ClientFeedSubscriptionModel,
        offsetAccountId: string,
    ) {
        super(sesService);
        this.bundles = bundles;
        this.clientAccountAndBundleData = clientAccountAndBundleData;
        this.offsetAccountId = offsetAccountId;
        this.currencyService = currencyService;
    }

    private static preparePlaceHolder(bundle: MarketDataFeedBundleInterface): string {
        return `${bundle.name} -
        ${SesHelper.getPriceDataBy(bundle.priceList, FeedPriceTypesEnum.SelfPro, false).displayPrice} -
        ${SesHelper.getPriceDataBy(bundle.priceList, FeedPriceTypesEnum.SelfNonPro, false).displayPrice} -
        ${SesHelper.getPriceCurrency(bundle.priceList)}`;
    }

    initForm(): void {
        this.formGroup = new FormGroup({});
        const bundleControls = CreateBundleSubscriptionControls();

        for (const bundle of this.bundles) {
            bundleControls.push(
                new CheckboxControl({
                    name: `${this.FEEDID_CHECKBOX_PREFIX}${bundle.id}`,
                    value: bundle.id,
                    placeholder: CreateSubscriptionForm.preparePlaceHolder(bundle),
                    validators: [],
                    componentType: ComponentTypesEnum.Checkbox,
                    floatingLabelNever: true,
                    checked: false,
                    color: ColorCodesEnum.Primary,
                    expectBooleanValue: false
                })
            );
        }

        this.controls = bundleControls.map((control: BaseControl) => {
            this.formGroup.addControl(control.name, new FormControl(control.value, control.validators));

            return control;
        });
        this.prepareCurrencyData();
    }

    private prepareCurrencyData(): void {
        const currencies = []; 
        const control = this.controls[2] as SelectControl;
        control.options = CurrencyTrait.getSortedCurrencyAsOptions(this.currencyService.getAllCurrencies());
        currencies.push(...this.currencyService.getAllCurrencies().map(x => x.currencyCode)); 
        control.extendValidators(InArrayValidator(currencies));  
        control.disabled = control.options.length === 1;
    }

    createRequest(): ServerResponse<boolean> {
        const activationDate = this.formGroup.value[CreateBundleSubscriptionActivationDateSelector];
        const price = this.formGroup.value[CreateBundleSubscriptionPriceSelector] ? this.getFormPriceValueByKey(CreateBundleSubscriptionPriceSelector): null;
        const currency = this.formGroup.value[CreateBundleSubscriptionCurrencySelector];
        const bundleIds: number[] = [];

        for (const property in this.formGroup.value) {
            if (property.startsWith(this.FEEDID_CHECKBOX_PREFIX) && this.formGroup.value[property] > 0) {
                bundleIds.push(this.formGroup.value[property]);
            }
        }

        if (bundleIds.length < this.MIN_SELECTED_BUNDLE_CCOUNT) {
            return Helper.errorsHelper().createBadRequestResponseObservable(
                MarketDataFeedBundleFormErrorStatesEnum.CreateMarketDataFeedBundleFailedNoFeed
            );
        }

        const model = new UserAccountSubscriptionInputModel(
            String(this.clientAccountAndBundleData.userId),
            this.clientAccountAndBundleData.accountReference,
            bundleIds,
            currency,
            null,
            0,
            price,
            activationDate,
            null
        );

        return this.sesService.createUserAccountSubscriptions(model).pipe(
            map((registerResponse: ServerResponseData<any>) => {
                if (registerResponse.status !== HttpStatusCodesEnum.Ok) {
                    return Helper.errorsHelper().createErrorResponse<any>(
                        registerResponse,
                        MarketDataFeedSubscriptionFormErrorStatesEnum.CreateMarketDataFeedSubscriptionFailed
                    );
                }

                return Helper.createSuccessResponse<boolean>(true);
            })
        );
    }
}
